import phrService from '@/apis/phr/service';
import createStore from '@/stores/createStore';
import { IRequesterResponse } from '@/utils/requester';
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import React from 'react';
import { StoreApi, StateSelector, useStore } from 'zustand';
import _ from 'lodash';
import { useParams } from 'react-router-dom';
interface IPhrStore {
  uid: string;
  phr?: IPhrData;
  member?: IMember;
  user?: IUser;
  phrObjComponent?: Record<string, IEMRData>;
  refetchPhr: (options?: RefetchOptions | undefined) => Promise<QueryObserverResult<IRequesterResponse<any>, Error>>;
}

const PhrMobileStoreContext = React.createContext<StoreApi<IPhrStore> | null>(null);

interface PhrMobileStoreProviderProps {
  children: React.ReactNode;
}

const PhrMobileStoreProvider: React.FC<PhrMobileStoreProviderProps> = ({ children }) => {
  const storeRef = React.useRef<StoreApi<IPhrStore>>();
  const params = useParams();
  const uid = params.uid as string;
  const { data, refetch } = phrService.useGetPhr({ uid });

  if (!storeRef.current) {
    storeRef.current = createStore<IPhrStore>(() => ({
      refetchPhr: refetch,
      uid: params?.uid || '',
      phr: undefined,
      user: undefined,
    }));
  }

  React.useEffect(() => {
    const phr: IPhrData = _.clone(data?.data);

    if (phr) {
      const objMEmber = [...(phr?.user?.family?.members || []), phr?.user?.family?.protector]?.reduce((d: any, u) => {
        d[u?._id] = u;
        return d;
      }, {});
      _.set(
        phr,
        'user.family.relationsWithMe',
        phr.user?.family.relations?.reduce((d: Record<string, IRelation>, r: IRelation) => {
          if (r.source === uid) {
            r.value = r.value?.split(' - ')?.[1];
            r.username = objMEmber?.[r.destination]?.username;
            // console.log(objMEmber);
            d[r.destination] = r;
          }
          return d;
        }, {}),
      );
      const user = phr.user;
      const phrObjComponent: Record<string, IEMRData> = (phr?.components || [])?.reduce((d: any, e: any) => {
        d[e?.type?.code] = e;
        return d;
      }, {});

      storeRef.current?.setState((s) => ({ ...s, uid: user?._id, user, phr, phrObjComponent }));
    }
  }, [data?.data, uid]);

  return <PhrMobileStoreContext.Provider value={storeRef.current}>{children}</PhrMobileStoreContext.Provider>;
};

function usePhrMobileStoreInContext<T = IPhrStore>(selector?: StateSelector<IPhrStore, T>): T {
  const store = React.useContext(PhrMobileStoreContext);

  if (!store) {
    throw new Error('Missing StoreProvider');
  }

  if (selector) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useStore(store, selector) as T;
  } else {
    return store.getState() as T;
  }
}

export { PhrMobileStoreProvider, usePhrMobileStoreInContext };
